import localizeStripePrice, {
  getStripePriceAmountAndCurrency,
} from '@app-studio-web/ui/helpers/localizeStripePrice'
import { type GetPricesData } from '@app-studio-web/ui/loaders/getPricesLoader'
import { type Plan, type StripePrice } from '@app-studio-web/ui/types'

/** A plan the paywall can offer, paired with its full and introductory prices. */
export interface PaywallPlan {
  full: StripePrice
  intro: StripePrice | undefined
  plan: Plan
}

// The order the baseline plans page renders its cards in: the highlighted top
// offer, then the monthly plan, then any second subscription and the one-time
// box that historically sat after it.
const PLAN_ORDER: Plan[] = ['top', 'first', 'second', 'one-time']

function getPlanPrices(
  data: GetPricesData,
  plan: Plan,
): { full: StripePrice | undefined; intro: StripePrice | undefined } {
  switch (plan) {
    case 'top':
      return { full: data.topPrice, intro: data.topIntroPrice }
    case 'first':
      return { full: data.firstPrice, intro: data.firstIntroPrice }
    case 'second':
      return { full: data.secondPrice, intro: data.secondIntroPrice }
    case 'one-time':
      return { full: data.oneTimePrice, intro: undefined }
    default: {
      const exhaustiveCheck: never = plan
      throw new Error(`Unhandled plan: ${String(exhaustiveCheck)}`)
    }
  }
}

export function getAvailablePlans(data: GetPricesData): PaywallPlan[] {
  return PLAN_ORDER.flatMap((plan) => {
    const { full, intro } = getPlanPrices(data, plan)

    return full ? [{ full, intro, plan }] : []
  })
}

/** How many days a price covers, matching the shared plan card's arithmetic. */
function getDaysFor(stripePrice: StripePrice): number {
  if (!stripePrice.recurring) return 1

  const { interval, interval_count: intervalCount } = stripePrice.recurring

  switch (interval) {
    case 'week':
      return intervalCount * 7
    case 'month':
      return intervalCount * 30
    case 'year':
      return intervalCount * 365
    default:
      return intervalCount
  }
}

function getPerDayCents({
  currency,
  stripePrice,
}: {
  currency: string
  stripePrice: StripePrice
}): number {
  const { stripePriceAmountInCents } = getStripePriceAmountAndCurrency({
    stripePrice,
    userCurrency: currency,
  })

  return stripePriceAmountInCents / getDaysFor(stripePrice)
}

interface PlanDisplay {
  /** Per-day rate of the full price, used as the strikethrough anchor. */
  anchorPerDay: string | undefined
  isOneTime: boolean
  perDay: string | undefined
  /** Whole-period price the customer is charged today. */
  periodPrice: string | undefined
  /** Discount off the full per-day rate, rounded down, or 0 when there is none. */
  savePercent: number
}

export function getPlanDisplay({
  currency,
  paywallPlan,
}: {
  currency: string
  paywallPlan: PaywallPlan
}): PlanDisplay {
  const { full, intro, plan } = paywallPlan
  const chargedPrice = intro ?? full

  const chargedPerDayCents = getPerDayCents({
    currency,
    stripePrice: chargedPrice,
  })
  const fullPerDayCents = getPerDayCents({ currency, stripePrice: full })
  const hasDiscount = fullPerDayCents > chargedPerDayCents

  return {
    anchorPerDay: hasDiscount
      ? localizeStripePrice({
          fraction: getDaysFor(full),
          stripePrice: full,
          userCurrency: currency,
        })
      : undefined,
    isOneTime: plan === 'one-time',
    perDay: localizeStripePrice({
      fraction: getDaysFor(chargedPrice),
      stripePrice: chargedPrice,
      userCurrency: currency,
    }),
    periodPrice: localizeStripePrice({
      stripePrice: chargedPrice,
      userCurrency: currency,
    }),
    savePercent: hasDiscount
      ? Math.floor((1 - chargedPerDayCents / fullPerDayCents) * 100)
      : 0,
  }
}
